import React, { useState } from "react";
import { X, Users, Clock, Copy, CheckCircle2 } from "lucide-react";
import { ScheduleItem } from "../types";
import { TYPE_COLORS } from "../constants";
import { getDaysDiff } from "../utils";

interface ScheduleModalProps {
  item: ScheduleItem;
  onClose: () => void;
}

const ScheduleModal: React.FC<ScheduleModalProps> = ({ item, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!item) return null;
  const typeConfig = TYPE_COLORS[item.type] || { label: item.type, bg: 'bg-gray-500', border: 'border-gray-500', text: 'text-gray-600' };
  const handleCopy = () => {
    navigator.clipboard.writeText(item.groupCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity animate-in fade-in duration-200" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className={`h-2 w-full ${typeConfig.bg}`}></div>
        <div className="px-6 py-5 flex items-start justify-between">
           <div className="flex-1 mr-4">
             <div className="flex items-center gap-2 mb-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${typeConfig.text} ${typeConfig.border} bg-white shadow-sm`}>{typeConfig.label}</span>
                {item.sourceSheet.toLowerCase().includes('闲鱼') && <span className="text-[10px] bg-amber-100 text-amber-700 px-2.5 py-0.5 rounded-full font-bold border border-amber-200">闲鱼</span>}
             </div>
             <div className="group flex items-center gap-3">
               <h3 className="text-2xl font-bold text-gray-900 leading-tight break-all">{item.groupCode}</h3>
               <button onClick={handleCopy} className="text-gray-400 hover:text-blue-500 transition-colors p-1.5 rounded-md hover:bg-blue-50" title="复制群号">{copied ? <CheckCircle2 size={18} className="text-green-500" /> : <Copy size={18} />}</button>
             </div>
           </div>
           <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors flex-shrink-0"><X size={24} /></button>
        </div>
        <div className="px-6 pb-8 overflow-y-auto space-y-6">
          <div className="grid grid-cols-2 gap-4">
             <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex flex-col justify-center hover:border-blue-200 transition-colors group/card">
                <span className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 group-hover/card:text-blue-500 transition-colors"><Users size={14} /> 承接老师</span>
                <span className="text-lg font-bold text-slate-800 truncate" title={item.teacher}>{item.teacher}</span>
             </div>
             <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex flex-col justify-center hover:border-emerald-200 transition-colors group/card">
                <span className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5 group-hover/card:text-emerald-500 transition-colors"><Users size={14} /> 学员规模</span>
                <span className="text-lg font-bold text-slate-800">{item.studentCount || '-'} <span className="text-sm font-medium text-slate-400 ml-1">人</span></span>
             </div>
          </div>
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
             <div className="bg-slate-50/50 px-4 py-3 border-b border-slate-200 flex items-center gap-2"><Clock size={16} className="text-blue-500" /><span className="font-bold text-slate-700 text-sm">排班生命周期</span></div>
             <div className="p-5">
               <div className="flex items-center justify-between relative mb-2">
                  <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-slate-100 -z-10"></div>
                  <div className="flex flex-col items-center bg-white px-2 z-10">
                     <span className="w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-sm mb-2"></span>
                     <span className="text-lg font-mono font-bold text-slate-800 leading-none">{item.startDate.getMonth() + 1}/{item.startDate.getDate()}</span>
                     <span className="text-[10px] text-slate-400 font-medium mt-1">{item.startDate.getFullYear()} Start</span>
                  </div>
                  <div className="flex flex-col items-center z-10 bg-white px-2"><span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full border border-slate-200">共 {getDaysDiff(item.startDate, item.endDate) + 1} 天</span></div>
                  <div className="flex flex-col items-center bg-white px-2 z-10">
                     <span className="w-3 h-3 rounded-full bg-slate-300 border-2 border-white shadow-sm mb-2"></span>
                     <span className="text-lg font-mono font-bold text-slate-800 leading-none">{item.endDate.getMonth() + 1}/{item.endDate.getDate()}</span>
                     <span className="text-[10px] text-slate-400 font-medium mt-1">{item.endDate.getFullYear()} End</span>
                  </div>
               </div>
             </div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end"><button onClick={onClose} className="px-6 py-2.5 bg-white border border-slate-300 hover:bg-slate-100 hover:border-slate-400 text-slate-700 text-sm font-bold rounded-xl shadow-sm transition-all active:scale-95">关闭</button></div>
      </div>
    </div>
  );
};

export default ScheduleModal;
